import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './views/login/Login';
import Users from './views/users/Users';
import Categories from './views/categories/Categories';
import Shows from './views/shows/Shows';
import UserLogs from './views/userlogs/UserLogs';
import Admins from './views/admins/Admins';
import Analytics from './views/analytics/Analytics';
import { Box, CircularProgress } from '@mui/material';

const theme = createTheme({
  palette: {
    primary: { main: '#fec958' },
    background: { default: '#f8f9fc' },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
    },
  },
});

function AuthGuard({ children }) {
  const { isAuthenticated, isInitialised } = useAuth();

  if (!isInitialised) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function GuestGuard({ children }) {
  const { isAuthenticated, isInitialised } = useAuth();
  if (!isInitialised) return null;
  if (isAuthenticated) return <Navigate to="/users" replace />;
  return children;
}

function SuperAdminGuard({ children }) {
  const { user } = useAuth();
  if (user?.userType !== 'superadmin') return <Navigate to="/users" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <GuestGuard>
                  <Login />
                </GuestGuard>
              }
            />
            <Route
              path="/"
              element={
                <AuthGuard>
                  <Layout />
                </AuthGuard>
              }
            >
              <Route index element={<Navigate to="/users" replace />} />
              <Route path="users" element={<Users />} />
              <Route path="categories" element={<Categories />} />
              <Route path="shows" element={<Shows />} />
              <Route path="userlogs" element={<UserLogs />} />
              <Route
                path="analytics"
                element={
                  <SuperAdminGuard>
                    <Analytics />
                  </SuperAdminGuard>
                }
              />
              <Route path="admins" element={<Admins />} />
            </Route>
            <Route path="*" element={<Navigate to="/users" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
